import { TRUSTED_DEVICE_TOKEN_STORAGE_KEY } from '../constants/auth';
import { Credentials, Nullable, User } from '../types';
import { SignupFields, Referral } from '../types/signup';
import { convertDictKeysToSnakeCase } from '../utils/common';
import { InvitationSignupValues } from '../viewsv2/UserManagement/InvitationSignup';
import axios from './axios';
import { SignUpResponse, Tokens } from './types';

export function refreshToken(refreshToken: Tokens['refresh']): Promise<Tokens> {
  return axios.post<Tokens>('/auth/token/refresh/', { refresh: refreshToken }).then(({ data }) => data);
}

export function tokenApi(credentials: Credentials): Promise<Tokens> {
  const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_TOKEN_STORAGE_KEY);

  return axios
    .post(
      `/auth/token/`,
      {
        email: credentials.email,
        password: credentials.password,
      },
      {
        headers: {
          ...(trustedDeviceToken && { 'Trusted-Device-Token': trustedDeviceToken }),
        },
      }
    )
    .then(({ data }) => data);
}

export function userDetailApi(): Promise<User> {
  return axios.get(`/users/me/`).then(({ data }) => data);
}

export function setUserPassword(token: string, password: string): Promise<Tokens> {
  return axios
    .post<Tokens>(`/password/set-password`, {
      token,
      password,
    })
    .then(({ data }) => data);
}

export function signUp(
  formValues: SignupFields,
  { id, source }: Referral,
  page: Nullable<string>,
  language: string,
  referralClient: Nullable<string>,
  hubspotCompanyId: Nullable<string>
) {
  return axios
    .post<SignUpResponse>('/signup/', {
      ...convertDictKeysToSnakeCase(formValues),
      utm_landing_page: page,
      ...(id ? { specified_referral: source, referral_id: id } : {}),
      language,
      ...(referralClient ? { company_user_referral_id: referralClient } : {}),
      hubspotCompanyId,
    })
    .then(({ data }) => data);
}

export function signupWithInvitation(
  values: Partial<InvitationSignupValues> & { name: string; contactPhone: string; token: string }
) {
  return axios
    .post<{ refresh: string; access: string }>(`/signup/with-invitation/`, values)
    .then(({ data }) => data);
}

export function acceptInvitation(token: string): Promise<{ success: boolean; companyId: number }> {
  return axios
    .post<{ success: boolean; companyId: number }>(`/companies/accept_invitation/${token}/`)
    .then(({ data }) => data);
}

export function validateTfa(
  code: string,
  rememberDevice = false
): Promise<{ isValid: boolean; tokens: Tokens; trustedDeviceToken: Nullable<string> }> {
  return axios
    .post<{ isValid: boolean; tokens: Tokens; trustedDeviceToken: Nullable<string> }>(
      `/users/me/validate_2fa/`,
      {
        code,
        rememberDevice,
      }
    )
    .then(({ data }) => data);
}

export function confirmTfa(
  code: string
): Promise<{ confirmed: boolean; tokens: Tokens; recoveryCode: string }> {
  return axios
    .post<{ confirmed: boolean; tokens: Tokens; recoveryCode: string }>(`/users/me/confirm_2fa/`, {
      code,
    })
    .then(({ data }) => data);
}

export function set2Fa(): Promise<{ qrCode: string; secret: string; provisioningUrl: string }> {
  return axios
    .post<{ qrCode: string; secret: string; provisioningUrl: string }>(`/users/me/set_2fa/`)
    .then(({ data }) => data);
}

export const validateRecoveryCode = async (recoveryCode: string) => {
  return axios
    .post<{ isValid: boolean }>(`/users/me/verify_recovery_code/`, { code: recoveryCode })
    .then(({ data }) => data);
};

export const generateNewRecoveryCode = async (twoFactorCode: string) => {
  return axios
    .post<{ recoveryCode: string }>(`/users/me/generate_recovery_code/`, { code: twoFactorCode })
    .then(({ data }) => data);
};
